import { ResponseMappingError, reqArray, reqRecord } from '@shared/value-objects/parse-helpers';
import {
  BULK_SALES_CHANNELS,
  type BulkSalesChannel,
  type BulkSalesChannelDecision,
  type BulkSalesContactRoute,
  type BulkSalesExclusionSource,
  type BulkSalesRouteDecision,
  type BulkSalesRouteOwner,
  type BulkSalesTargetState,
  type FirmBulkSalesDecision,
  type LawyerBulkSalesDecision,
} from './model';

/// 派生レスポンス `bulk_sales_decision` の門番 (parse, don't validate)。backend が常に
/// 供給する契約のため、欠落・不正は契約破れとして throw する。

const STATES: readonly BulkSalesTargetState[] = ['target', 'partially_excluded', 'excluded'];
const SOURCES: readonly BulkSalesExclusionSource[] = ['lawyer', 'firm', 'disciplinary'];
const ROUTES: readonly BulkSalesContactRoute[] = [
  'lawyer_emails',
  'lawyer_direct_line',
  'firm_email',
  'firm_phone',
  'firm_fax',
  'firm_address',
];
const OWNERS: readonly BulkSalesRouteOwner[] = ['lawyer', 'firm'];

function oneOf<T extends string>(raw: unknown, allowed: readonly T[], field: string): T {
  if (typeof raw === 'string' && (allowed as readonly string[]).includes(raw)) return raw as T;
  throw new ResponseMappingError(field, `must be one of ${allowed.join(' / ')}`);
}

function listOf<T extends string>(raw: unknown, allowed: readonly T[], field: string): T[] {
  return reqArray(raw, field).map((v, i) => oneOf(v, allowed, `${field}[${i}]`));
}

function parseRoute(raw: unknown, field: string): BulkSalesRouteDecision {
  const o = reqRecord(raw, field);
  if (typeof o.excluded !== 'boolean') {
    throw new ResponseMappingError(`${field}.excluded`, 'must be a boolean');
  }
  return {
    route: oneOf(o.route, ROUTES, `${field}.route`),
    owner: oneOf(o.owner, OWNERS, `${field}.owner`),
    excluded: o.excluded,
    sources: listOf(o.sources, SOURCES, `${field}.sources`),
  };
}

function parseChannel(raw: unknown, field: string): BulkSalesChannelDecision {
  const o = reqRecord(raw, field);
  return {
    state: oneOf(o.state, STATES, `${field}.state`),
    sources: listOf(o.sources, SOURCES, `${field}.sources`),
    routes: reqArray(o.routes, `${field}.routes`).map((r, i) =>
      parseRoute(r, `${field}.routes[${i}]`),
    ),
  };
}

export function parseLawyerBulkSalesDecision(raw: unknown, field: string): LawyerBulkSalesDecision {
  const o = reqRecord(raw, field);
  const channelsRaw = reqRecord(o.channels, `${field}.channels`);
  const channels = Object.fromEntries(
    BULK_SALES_CHANNELS.map((c) => [c, parseChannel(channelsRaw[c], `${field}.channels.${c}`)]),
  ) as Record<BulkSalesChannel, BulkSalesChannelDecision>;
  return { state: oneOf(o.state, STATES, `${field}.state`), channels };
}

export function parseFirmBulkSalesDecision(raw: unknown, field: string): FirmBulkSalesDecision {
  const o = reqRecord(raw, field);
  return {
    state: oneOf(o.state, STATES, `${field}.state`),
    excluded_channels: listOf(o.excluded_channels, BULK_SALES_CHANNELS, `${field}.excluded_channels`),
  };
}
